import { ConversationStatus, InterviewLanguageConfig } from '@/types/pulse';
import {
  Interview,
  InterviewResult,
  InterviewWithStats
} from '@/types/pulsedb';
import { VoiceConfig } from '@/types/pulse';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getInterviewsWithStats = cache(
  async (
    supabase: SupabaseClient,
    workspaceId: string,
    limit?: number
  ): Promise<InterviewWithStats[] | null> => {
    // First get interviews
    let query = supabase
      .from('interviews')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('deleted', false)
      .order('id', { ascending: false })
      .throwOnError();

    if (limit) query = query.limit(limit);
    const { data: interviews } = await query;

    if (!interviews || interviews.length === 0) return [];

    const interviewIds = interviews.map((i) => i.id);

    // Get conversation stats for all interviews in one query
    const { data: stats } = await supabase
      .from('conversations')
      .select('interview_id, ended_at')
      .in('interview_id', interviewIds)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('ended_at', { ascending: false })
      .throwOnError();

    // Group stats by interview_id
    const statsMap = new Map<
      number,
      { last_completed_at: string | null; completed_count: number }
    >();

    if (stats) {
      for (const stat of stats) {
        const existing = statsMap.get(stat.interview_id);
        if (existing) {
          existing.completed_count++;
        } else {
          statsMap.set(stat.interview_id, {
            last_completed_at: stat.ended_at,
            completed_count: 1
          });
        }
      }
    }

    // Merge interviews with stats
    return interviews.map((interview) => ({
      ...interview,
      last_completed_at: statsMap.get(interview.id)?.last_completed_at || null,
      completed_count: statsMap.get(interview.id)?.completed_count || 0
    }));
  }
);

export const getRecentInterviews = cache(
  async (
    supabase: SupabaseClient,
    workspaceId: string,
    limit: number = 5
  ): Promise<Interview[] | null> => {
    const { data } = await supabase
      .from('interviews')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit)
      .throwOnError();
    return data;
  }
);

export const getInterviewBySlug = async (
  supabase: SupabaseClient,
  slug: string
): Promise<Interview | null> => {
  const { data } = await supabase
    .from('interviews')
    .select('*')
    .eq('slug', slug)
    .eq('deleted', false)
    .maybeSingle()
    .throwOnError();
  return data;
};

export const getInterview = async (
  supabase: SupabaseClient,
  id: number
): Promise<Interview | null> => {
  const resp = await supabase
    .from('interviews')
    .select('*')
    .eq('id', id)
    .eq('deleted', false)
    .single()
    .throwOnError();
  return resp.data;
};

export const getInterviewWorkspaceId = async (
  supabase: SupabaseClient,
  id: number
): Promise<string | null> => {
  const resp = await supabase
    .from('interviews')
    .select('workspace_id')
    .eq('id', id)
    .eq('deleted', false)
    .single()
    .throwOnError();
  return resp.data?.workspace_id ?? null;
};

export const getInterviewVoiceConfig = async (
  supabase: SupabaseClient,
  id: number
): Promise<VoiceConfig | null> => {
  const { data } = await supabase
    .from('interviews')
    .select('voice_config')
    .eq('id', id)
    .maybeSingle()
    .throwOnError();
  return (data?.voice_config as VoiceConfig | null) ?? null;
};

export const getInterviewLocale = async (
  supabase: SupabaseClient,
  id: number
): Promise<string | null> => {
  const { data } = await supabase
    .from('interviews')
    .select('language_config')
    .eq('id', id)
    .maybeSingle()
    .throwOnError();
  const config = data?.language_config as InterviewLanguageConfig | null;
  return config?.locale ?? null;
};

export const getInterviewForConversation = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<Interview | null> => {
    const { data: conversation } = await supabase
      .from('conversations')
      .select('interview_id')
      .eq('id', conversationId)
      .single()
      .throwOnError();
    if (!conversation?.interview_id) return null;
    return getInterview(supabase, conversation.interview_id);
  }
);

/**
 * Updates the existing interview_results row if present, inserts otherwise.
 */
export const upsertInterviewResult = async (
  supabase: SupabaseClient,
  interviewId: number,
  fields: Partial<InterviewResult>
): Promise<void> => {
  const { data: existing } = await supabase
    .from('interview_results')
    .select('id')
    .eq('interview_id', interviewId)
    .limit(1)
    .maybeSingle()
    .throwOnError();

  if (!existing) {
    await supabase
      .from('interview_results')
      .insert({ interview_id: interviewId, ...fields })
      .throwOnError();
  } else {
    await supabase
      .from('interview_results')
      .update(fields)
      .eq('interview_id', interviewId)
      .throwOnError();
  }
};
